import { Col, Row } from 'antd'
import cx from 'classnames'
import './AQLSummary.style.less'
import { get, includes, isEmpty, size } from 'lodash'
import { CLASSIFICATION_ENUM } from '@/constant/status'
import { isEmptyValue } from '@/utils/common'

type DefectType = {
  count: number
  tolerance: number
  classification: CLASSIFICATION_ENUM
  is_out_of_tolerance: boolean
}
type MergedSummaryDefect = {
  defectName: string
  critical: number
  major: number
  minor: number
  other: number
}
type DefectSummaryDataType = {
  critical: DefectType
  major: DefectType
  minor: DefectType
  other: DefectType
}
type DefectItem = {
  classification: CLASSIFICATION_ENUM
  defect_list?: { name?: string } | null
  quantity: number
  item?: unknown
}
type DefectsDataType = DefectItem[]
type DataType = {
  defectSummary: DefectSummaryDataType
  defects: DefectsDataType
  inspection?: { show_defect_breakdown?: boolean }
}
type CombinedDefectsAql = {
  major: string
  minor: string
}
type InspectionAqlType = {
  major_level?: string
  minor_level?: string
}
interface Props {
  data: DataType
  option: any
  inspectionAql?: InspectionAqlType | null
  combinedDefectsAqlSetting?: CombinedDefectsAql[]
}
const acceptance = [
  CLASSIFICATION_ENUM.Critical,
  CLASSIFICATION_ENUM.Major,
  CLASSIFICATION_ENUM.Minor,
]

const getHigherColor = (dataDefect: DefectType, isForceDefault = false) => {
  if (isForceDefault) {
    return ''
  }
  const { is_out_of_tolerance } = dataDefect
  const highlightRed = includes(acceptance, dataDefect.classification)
  return highlightRed && is_out_of_tolerance ? 'over-defect-color' : ''
}

const isSameAqlLevel = (a?: string, b?: string) => {
  if (isEmptyValue(a) || isEmptyValue(b)) return false
  const numA = Number(a)
  const numB = Number(b)
  if (!isNaN(numA) && !isNaN(numB)) return numA === numB
  return String(a).trim().toUpperCase() === String(b).trim().toUpperCase()
}

const shouldCombineMajorMinor = (
  inspectionAql?: InspectionAqlType | null,
  combinedDefectsAqlSetting: CombinedDefectsAql[] = []
) => {
  if (!inspectionAql || !size(combinedDefectsAqlSetting)) return false
  return combinedDefectsAqlSetting.some(
    (item) =>
      isSameAqlLevel(item.major, inspectionAql.major_level) &&
      isSameAqlLevel(item.minor, inspectionAql.minor_level)
  )
}

const getSummaryDefectBreakdown = (defectsData: any): MergedSummaryDefect[] => {
  if (!size(defectsData)) return []
  const mergedDefects = defectsData?.reduce((acc, curr) => {
    const defectName = curr.defect_list?.name
    if (!defectName) return acc
    const classification = curr.classification
    const quantity = curr.quantity || 0

    if (!acc[defectName]) {
      acc[defectName] = {
        defectName,
        critical: 0,
        major: 0,
        minor: 0,
        other: 0,
      }
    }

    switch (classification) {
      case CLASSIFICATION_ENUM.Critical:
        acc[defectName].critical += quantity
        break
      case CLASSIFICATION_ENUM.Major:
        acc[defectName].major += quantity
        break
      case CLASSIFICATION_ENUM.Minor:
        acc[defectName].minor += quantity
        break
      default:
        acc[defectName].other += quantity
        break
    }

    return acc
  }, {} as Record<string, MergedSummaryDefect>)

  return Object.values(mergedDefects)
}

function AQLSummary(props: Props) {
  const data: any = get(props, 'data', {})
  const hideAqlMaxStatus = get(
    props,
    'option.inspection.detail.aqlMaxRow.status',
    false
  )
  const hideAqlAboveStatus = get(
    props,
    'option.inspection.detail.aqlAboveRow.status',
    false
  )
  const isForceDefault = hideAqlMaxStatus || hideAqlAboveStatus
  const { critical, major, minor, other } = data.defectSummary
  const show_defect_breakdown = Boolean(
    props.data?.inspection?.show_defect_breakdown
  )
  const defectSummaries = getSummaryDefectBreakdown(props.data.defects)
  const isCombineMajorMinor =
    show_defect_breakdown &&
    shouldCombineMajorMinor(
      props.inspectionAql,
      props.combinedDefectsAqlSetting
    )
  const sectionHeaderClass = show_defect_breakdown
    ? 'section-header-container'
    : 'section-reported-container'
  const titleColNumber = show_defect_breakdown ? 12 : 8
  const valueColNumber = show_defect_breakdown ? 3 : 4
  const handleRenderHeaderItem = () => {
    const handleRenderCol = (dataDefect: DefectType, title: string) => {
      const textHighlightRed = getHigherColor(dataDefect, isForceDefault)
      return (
        <Col span={valueColNumber}>
          <p
            className={cx(`header ${textHighlightRed}`)}
            style={{ textAlign: 'center' }}
          >
            {title}
          </p>
        </Col>
      )
    }

    return (
      <Row
        className="section-header-container bottom-border"
        justify={'space-between'}
      >
        <Col span={titleColNumber}>
          <p className="header" style={{ textAlign: 'left' }}>
            Defects
          </p>
        </Col>
        {handleRenderCol(critical, CLASSIFICATION_ENUM.Critical)}
        {handleRenderCol(major, CLASSIFICATION_ENUM.Major)}
        {handleRenderCol(minor, CLASSIFICATION_ENUM.Minor)}
        {handleRenderCol(other, CLASSIFICATION_ENUM.Other)}
      </Row>
    )
  }
  const handleRenderSummaryDefectItem = (
    summaryDefectItem: MergedSummaryDefect
  ) => {
    // Removed debug logging for production
    const handleRenderCol = (dataDefect: DefectType, defectCount: number) => {
      const textHighlightRed = getHigherColor(dataDefect, isForceDefault)
      return (
        <Col span={valueColNumber}>
          <p
            className={cx(`value ${textHighlightRed}`)}
            style={{ textAlign: 'center' }}
          >
            {defectCount}
          </p>
        </Col>
      )
    }

    return (
      <Row
        className="section-reported-container bottom-border"
        justify={'space-between'}
      >
        <Col span={titleColNumber}>
          <p className="header" style={{ textAlign: 'left' }}>
            {summaryDefectItem.defectName}
          </p>
        </Col>
        {handleRenderCol(critical, summaryDefectItem.critical)}
        {handleRenderCol(major, summaryDefectItem.major)}
        {handleRenderCol(minor, summaryDefectItem.minor)}
        {handleRenderCol(other, summaryDefectItem.other)}
      </Row>
    )
  }

  const handleRenderReportItem = () => {
    const handleRenderCol = (dataDefect: DefectType) => {
      const textHighlightRed = getHigherColor(dataDefect, isForceDefault)
      return (
        <Col span={valueColNumber}>
          <p
            className={cx(`value ${textHighlightRed}`)}
            style={{ textAlign: 'center', fontWeight: 700 }}
          >
            {dataDefect.count ?? 0}
          </p>
        </Col>
      )
    }

    const handleRenderCombinedMajorMinorCol = () => {
      const isOutOfTolerance =
        major.is_out_of_tolerance || minor.is_out_of_tolerance
      const textHighlightRed =
        !isForceDefault && isOutOfTolerance ? 'over-defect-color' : ''
      return (
        <Col span={valueColNumber * 2}>
          <p
            className={cx(`value ${textHighlightRed}`)}
            style={{ textAlign: 'center', fontWeight: 700 }}
          >
            {(Number(major.count) || 0) + (Number(minor.count) || 0)}
          </p>
        </Col>
      )
    }

    const rowStyle = show_defect_breakdown ? { backgroundColor: '#F6F6F6' } : {}

    return (
      <Row
        className={sectionHeaderClass}
        justify={'space-between'}
        style={rowStyle}
      >
        <Col span={titleColNumber}>
          <p className="header" style={{ textAlign: 'left' }}>
            {show_defect_breakdown ? 'Total' : 'Reported'}
          </p>
        </Col>
        {handleRenderCol(critical)}
        {isCombineMajorMinor ? (
          handleRenderCombinedMajorMinorCol()
        ) : (
          <>
            {handleRenderCol(major)}
            {handleRenderCol(minor)}
          </>
        )}
        {handleRenderCol(other)}
      </Row>
    )
  }

  const handleRenderAqlMaxItem = () => {
    if (hideAqlMaxStatus) {
      return <></>
    }

    const handleRenderCol = (dataDefect: DefectType) => {
      const textHighlightRed = getHigherColor(dataDefect, isForceDefault)
      return (
        <Col span={valueColNumber}>
          <p
            className={cx(`value ${textHighlightRed}`)}
            style={{ textAlign: 'center', fontWeight: 700 }}
          >
            {dataDefect.tolerance ?? 0}
          </p>
        </Col>
      )
    }

    return (
      <Row className={sectionHeaderClass} justify={'space-between'}>
        <Col span={titleColNumber}>
          <p className="header" style={{ textAlign: 'left' }}>
            AQL Max
          </p>
        </Col>
        {handleRenderCol(critical)}
        {handleRenderCol(major)}
        {handleRenderCol(minor)}
        {handleRenderCol(other)}
      </Row>
    )
  }

  const handleRenderAboveAqlItem = () => {
    if (hideAqlAboveStatus) {
      return <></>
    }

    const handleRenderCol = (dataDefect: DefectType) => {
      const { count, tolerance, is_out_of_tolerance } = dataDefect
      const textHighlightRed = getHigherColor(dataDefect, isForceDefault)
      return (
        <Col span={valueColNumber}>
          <p
            className={cx(`header ${textHighlightRed}`)}
            style={{ textAlign: 'center' }}
          >
            {is_out_of_tolerance
              ? (Number(count) || 0) - (Number(tolerance) || 0)
              : 0}
          </p>
        </Col>
      )
    }

    return (
      <Row className={sectionHeaderClass} justify={'space-between'}>
        <Col span={titleColNumber}>
          <p className="header" style={{ textAlign: 'left' }}>
            Above AQL
          </p>
        </Col>
        {handleRenderCol(critical)}
        {handleRenderCol(major)}
        {handleRenderCol(minor)}
        {handleRenderCol(other)}
      </Row>
    )
  }

  if (isEmpty(props.data)) {
    return null
  }

  return (
    <div className="aql-summary-container page-break-inside">
      {handleRenderHeaderItem()}
      {show_defect_breakdown
        ? defectSummaries?.map((item) => handleRenderSummaryDefectItem(item))
        : null}
      {handleRenderReportItem()}
      {handleRenderAqlMaxItem()}
      {handleRenderAboveAqlItem()}
    </div>
  )
}

export default AQLSummary
